import { useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import VinDetail from "@/models/VinDetail";
import { chunkArray } from "@/utilities/chunkArray";

/** VINs per `DecodeVinsNew` call; a fleet-sized list is split rather than sent as one request. */
const VINS_PER_CALL = 500;

export function useDecodeVins(vins?: string[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  // Sorted, because this array is the query key: in caller order it changes whenever the same
  // vehicles arrive in a different order, and every VIN is decoded again.
  const uniqueIds = useMemo(() => (vins ? uniq(vins).sort() : []), [vins]);

  return useQuery<VinDetail[]>({
    queryKey: ["vins", activeSessionId, uniqueIds],

    queryFn: async ({ signal }) => {
      if (uniqueIds.length === 0) {
        return [];
      }

      const chunks = await Promise.all(
        chunkArray(uniqueIds, VINS_PER_CALL).map(
          async chunk =>
            ((await geotabClient.call("DecodeVinsNew", { vins: chunk }, signal)) ??
              []) as VinDetail[],
        ),
      );
      return chunks.flat();
    },
    enabled: !!uniqueIds.length,
  });
}
